const cache = require('node-cache');
const {cache_lifetime_in_seconds} = require('./config');
const {getSecret, setSecret} = require('./secretsLib');
const localCache = new cache({stdTTL: cache_lifetime_in_seconds || 300});
const config = require('./config');

/**
 * Gets the key vault url for the given input
 * @param {string} keyVault keyvault name or url
 * @returns If the input is a url, the url is returned. If the input is the 
 * keyvault name, then the formatted URL is returned.
 */
const getKeyVaultUrl = (keyVault) => {
    if(keyVault.indexOf('https://') !== 0) {
        return `https://${keyVault}.vault.azure.net`;
    }
    return keyVault;
}

/**
 * Get the secret from the local configuration.
 * @param {string} secretName The name of the setting to be pulled from the local configuration.
 * @returns the contents of the setting requested.
 */
 const getFromLocalConfig = (secretName) => {
    const secret = config.getLocalConfig(secretName);
    if (!secret) throw `
    ${('!').repeat(20)}
    
    ${secretName} is not set within the local configuration.
    The mode is currently set to development, if you are not using development, or need to use the remote app configurations please remove the 'development' configuration.

    ${('!').repeat(20)}`;
    return secret;
}

/**
 * Gets a secret from the remote keyvault. This method caches the secret
 * for the cache_lifetime_in_seconds set within the process.env.
 * @param {string} keyVault keyvault name or url
 * @param {string} secretName secret name to grab from the vault
 * @throws If there is an exception in the execution of this function
 * an error will be thrown.
 * @returns string: secret value
 */
const get = async (keyVault, secretName) => {
    const isProduction = !config.isDevelopment;
    if(!isProduction) return getFromLocalConfig(secretName);
    if(!keyVault || !secretName) throw `The keyVault and secretName are required parameters to get a secret.`;
    let cacheKey = `${keyVault}-${secretName}`;
    let cacheValue = localCache.get(cacheKey);
    const keyVaultUrl = getKeyVaultUrl(keyVault);
    if(!cacheValue) {
        const secret = await getSecret(keyVaultUrl, secretName);
        localCache.set(cacheKey, secret);
        cacheValue = secret;
    }
    return cacheValue;
}

/**
 * Sets the secret inside the keyvault and in the local cache.
 * @param {string} keyVault keyvault name or url
 * @param {string} secretName the name of the secret
 * @param {string} secretValue the value of the secret
 * @throws If there is an exception in the execution of this function
 * an error will be thrown.
 * @returns void
 */
const set = async (keyVault, secretName, secretValue) => {
    const isProduction = !config.isDevelopment;
    if(!isProduction) throw `You cannot set secrets when development mode is enabled.`;
    if(!keyVault || !secretName || !secretValue) throw `The keyVault and secretName are required parameters to get a secret.`;
    let cacheKey = `${keyVault}-${secretName}`;
    const keyVaultUrl = getKeyVaultUrl(keyVault);
    await setSecret(keyVaultUrl, secretName, secretValue);
    localCache.set(cacheKey, secretValue);
}

/**
 * Remove all cache entries in the local cache.
 */
const bustCache = () => {
    const cacheKeys = localCache.keys();
    for(let i = 0; i < cacheKeys.length; i++) {
        localCache.del(cacheKeys[i]);
    }
}

/**
 * invalidate the cache for a specific entry by name.
 * @param {string} keyVault The keyvault name where the secret is stored
 * @param {string} secretName the secret name
 * @returns string A message notifying the user of the result of the action, ex: 'yourSetting has been invalidated in the in memory cache'
 */
const bustCacheForKey = (keyVault, secretName) => {
    if(!keyVault || !secretName) throw `The keyVault and secretName are required parameters to clear the cache for a secret.`;
    let cacheKey = `${keyVault}-${secretName}`;
    if(localCache.has(cacheKey)) {
        localCache.del(`${keyVault}-${secretName}`);
        return `${secretName} has been invalidated in the in memory cache.`;
    }
    return `${secretName} is not cached.`;
}

module.exports = {get, set, bustCacheForKey, bustCache};